import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Radio, RadioGroup, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, sectionCardSx, listCardSx, insetPanelSx, sectionHeadingSx, emptyTextSx,
  actionRowSx, dialogProps, dialogTitleSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from '../securityhub/OperationProgress';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import {
  deleteStackSet, getStackSet, getStackSetTemplate, listInstances, listOperations,
  retryInstances, stopOperation, syncStackSet, getSyncPreview,
  deleteInstance, listStackSetsAccounts, removeExclusions,
  detectStackSetDrift, refreshStackSetStatus, getRefreshStatus, repairInstance,
} from '../../../services/stacksetsApi';
import { statusColor } from './StackSetsList';
import { formatStatusLabel } from '../../../utils/statusLabel';
import InstanceEventsRow, { isCancelledCell } from './InstanceEventsRow';
import OperationResultsRow from './OperationResultsRow';
import ResourceDriftPanel from './ResourceDriftPanel';

// How long a StackSet delete keeps polling for its final delete_stackset
// operation after the status reads deleted.
const AWAIT_DELETE_MAX_MS = 2 * 60 * 1000;

// Instance drift status -> chip intent ("" = never checked).
const driftIntent = (s) => ({ IN_SYNC: 'success', DRIFTED: 'warning' }[s] || 'neutral');
const driftLabel = (s) => formatStatusLabel(s || 'NOT_CHECKED');
const REPAIRABLE = ['missing', 'role_missing'];
const INSTANCE_COLS = 8;

// "3 min ago" for the auto-deploy last-checked line.
const timeAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
};

const EXCLUSION_REASON_LABELS = {
  unselected: 'Unselected under a target OU',
  instance_deleted: 'Instance deleted',
};

const ACTIVE = ['in_progress', 'updating', 'deleting'];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// What an action's in-flight banner turns into once the StackSet is idle
// again: {notice} (success), {error}, or null (nothing to add — e.g. the
// StackSet's own error message already explains a failure). Pure: reads only
// the freshly polled page data.
const pendingOutcome = (pending, dep, instances, operations) => {
  const cell = instances.find((r) => r.accountId === pending.account && r.region === pending.region);
  const where = `${pending.account} / ${pending.region}`;
  switch (pending.kind) {
    case 'drift': {
      // Operations are newest-first.
      const op = operations.find((o) => o.type === 'detect_drift');
      if (!op) return null;
      if (op.status === 'failed' || op.status === 'stopped') {
        return { error: `Drift detection ${op.status}${op.errorMessage ? `: ${op.errorMessage}` : '.'}` };
      }
      const checked = instances.filter((r) => r.phase !== 'deleted' && r.phase !== 'skipped');
      const drifted = checked.filter((r) => r.driftStatus === 'DRIFTED').length;
      const summary = drifted
        ? `${plural(drifted, 'instance')} of ${checked.length} drifted.`
        : `no drift found on ${plural(checked.length, 'instance')}.`;
      if (op.status === 'partial') return { error: `Drift detection finished, but ${op.errorMessage || 'some drift results could not be read.'}` };
      return { notice: `Drift detection finished: ${summary}` };
    }
    case 'deleteInstance':
      if (cell && cell.phase === 'failed') {
        return { error: `The stack instance in ${where} could not be deleted${cell.statusReason ? `: ${cell.statusReason}` : '.'}` };
      }
      if (!cell || cell.phase === 'deleted') return { notice: `The stack instance in ${where} was deleted.` };
      return null;
    case 'deleteStackSet':
      return dep.status === 'deleted' ? { notice: 'StackSet deleted.' } : null;
    case 'repair':
      if (cell?.phase === 'current') return { notice: `Repaired ${where}.` };
      if (cell) {
        return { error: `Repair of ${where} did not succeed${cell.statusReason ? `: ${cell.statusReason}` : '.'}` };
      }
      return null;
    default: // sync, retry: progress and failures show on the StackSet itself
      return null;
  }
};

// Polls a background job (Sync preview, status refresh) until it leaves
// 'running'. The backend answers the starting request before resolving OU
// targets, because the gateway ends every request at 15s. Returns null when
// isCancelled() turns true (dialog closed, page left); a few transient poll
// failures are tolerated before giving up.
const waitForJob = async (job, fetchJob, isCancelled) => {
  let current = job;
  let misses = 0;
  while (current && current.status === 'running') {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, POLL_INTERVAL_MS); });
    if (isCancelled()) return null;
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetchJob();
      current = res?.data || null;
      misses = 0;
    } catch (e) {
      misses += 1;
      if (misses >= 3) throw e;
    }
  }
  return isCancelled() ? null : current;
};

// Admin-console tab styling: Metropolis 600, sentence case, accent on select.
const tabSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  minHeight: 56,
  color: CK.body,
  '&.Mui-selected': { color: CK.blue },
};

const tabsSx = {
  borderBottom: `1px solid ${CK.border}`,
  px: 2,
  '& .MuiTabs-indicator': { backgroundColor: CK.blue, height: 3 },
};

const dialogBodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

// Sub-label for the two groups inside the Targets section. Deliberately quieter
// than sectionHeadingSx: "Targets" is the section, OUs and accounts are its parts.
const targetGroupLabelSx = {
  fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 600, color: CK.body, mb: 0.5,
};

export default function StackSetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [dep, setDep] = useState(null);
  const [instances, setInstances] = useState([]);
  const [operations, setOperations] = useState([]);
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [error, setError] = useState(null); // page-blocking: refresh() failures only
  const [actionError, setActionError] = useState(null); // per-action failures — dismissible banner
  const [notice, setNotice] = useState(null);
  // The banner of an action that is still running ("Deleting…", "Drift
  // detection started", "Repairing…"): {kind, text, account, region, seq}.
  // Replaced by its outcome once the StackSet is idle again (see below).
  const [pending, setPending] = useState(null);
  // refresh() calls are numbered: an outcome is only read from data fetched by
  // a refresh STARTED after the action, never from a tick already in flight.
  const refreshSeqRef = useRef(0);
  const [loadedSeq, setLoadedSeq] = useState(0);
  // Highest refresh whose response was applied; an older response that lands
  // late is dropped so stale data can neither overwrite newer data nor settle
  // a pending banner.
  const appliedSeqRef = useRef(0);
  // When a deleted status was first seen during a StackSet delete (see
  // awaitingDelete).
  const deletedSeenAtRef = useRef(0);
  const pollRef = useRef(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [retain, setRetain] = useState('delete');
  const [confirmText, setConfirmText] = useState('');
  // Sync dialog state. The preview is a polled job: the dialog opens at once
  // and shows progress until the plan (or its failure) arrives.
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncStarting, setSyncStarting] = useState(false);
  const [syncRetain, setSyncRetain] = useState('delete');
  // Bumped to abandon an in-flight poll (dialog closed, new preview, unmount).
  const syncTokenRef = useRef(0);
  const refreshTokenRef = useRef(0);
  // Delete-one-instance dialog: {cell: 'account|region', retain}
  const [instDelete, setInstDelete] = useState(null);
  const [accountNames, setAccountNames] = useState({});
  const namesLoadedRef = useRef(false);
  // Drift / health
  const [refreshing, setRefreshing] = useState(false);
  const [driftOpen, setDriftOpen] = useState({}); // 'account|region' -> true
  const [repairTarget, setRepairTarget] = useState(null); // instance row
  const [repairing, setRepairing] = useState(false);

  const refresh = useCallback(async () => {
    refreshSeqRef.current += 1;
    const seq = refreshSeqRef.current;
    try {
      const [d, i, o] = await Promise.all([getStackSet(id), listInstances(id), listOperations(id)]);
      if (seq < appliedSeqRef.current) return;
      appliedSeqRef.current = seq;
      setDep(d?.data || null);
      setInstances(i?.data || []);
      setOperations(o?.data || []);
      setError(null);
      setLoadedSeq((cur) => Math.max(cur, seq));
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Abandon any job poll still running when the page goes away.
  useEffect(() => () => {
    syncTokenRef.current += 1;
    refreshTokenRef.current += 1;
  }, []);

  // Account names for the Exclusions card, fetched once and only when the
  // StackSet has exclusions (instance rows carry ids only).
  const hasExclusions = Boolean(dep?.excludedCells && dep.excludedCells !== '[]' && dep.excludedCells !== 'null');
  useEffect(() => {
    if (!hasExclusions || namesLoadedRef.current) return;
    namesLoadedRef.current = true;
    listStackSetsAccounts()
      .then((res) => {
        const map = {};
        (Array.isArray(res?.data) ? res.data : []).forEach((a) => { map[a.accountId] = a.name; });
        setAccountNames(map);
      })
      .catch(() => { /* names are a nicety; ids still render */ });
  }, [hasExclusions]);

  // Poll keyed on parent status (frozen-bar lesson: NOT on cell activity), and
  // on a running operation: drift detection runs without changing the status.
  const opRunning = operations.some((o) => o.status === 'running');
  // During a StackSet delete the instance-deletion step rolls the status up to
  // "deleted" a moment before the StackSet itself is deleted in AWS; keep
  // polling until that last step (the newest operation, delete_stackset) has
  // settled, so a failure there is not missed.
  //
  // Bounded: if the backend restarts mid-delete, no delete_stackset operation
  // ever appears, and the page must not poll forever waiting for it.
  const deletedSeen = pending?.kind === 'deleteStackSet' && dep?.status === 'deleted';
  if (!deletedSeen) deletedSeenAtRef.current = 0;
  else if (!deletedSeenAtRef.current) deletedSeenAtRef.current = Date.now();
  const awaitingDelete = deletedSeen
    && Date.now() - deletedSeenAtRef.current < AWAIT_DELETE_MAX_MS
    && !(operations[0]?.type === 'delete_stackset' && operations[0].status !== 'running');
  const shouldPoll = ACTIVE.includes(dep?.status) || opRunning || awaitingDelete;
  useEffect(() => {
    if (!shouldPoll || pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      await refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [shouldPoll, refresh]);

  // Stop the interval inside the tick when the parent settles.
  useEffect(() => {
    if (dep && !shouldPoll && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [dep, shouldPoll]);

  // Settle an action's in-flight banner: once a refresh started after the
  // action shows the StackSet idle (no busy status, no running operation),
  // swap the banner for the outcome.
  useEffect(() => {
    if (!pending || !dep || loadedSeq <= pending.seq || shouldPoll) return;
    setPending(null);
    const outcome = pendingOutcome(pending, dep, instances, operations);
    if (outcome?.error) setActionError(outcome.error);
    else if (outcome?.notice) setNotice(outcome.notice);
  }, [pending, dep, instances, operations, shouldPoll, loadedSeq]);

  // Show an action's banner until it finishes. Call after the action's request
  // returned (the backend has claimed the StackSet by then), before refresh().
  const startPending = useCallback((kind, text, cell = {}) => {
    setNotice(null);
    setPending({ kind, text, account: cell.account, region: cell.region, seq: refreshSeqRef.current });
  }, []);

  // Lazy template fetch. On failure, template stays null (so Retry re-runs
  // this) and templateError is surfaced in the tab; the in-flight flag
  // prevents double fetches from rapid tab clicks.
  const loadTemplate = useCallback(async () => {
    if (template !== null || loadingTemplate) return;
    setLoadingTemplate(true);
    setTemplateError(null);
    try {
      const res = await getStackSetTemplate(id);
      setTemplate(res?.data?.templateBody || '');
    } catch (e) {
      setTemplateError(e.message);
    } finally {
      setLoadingTemplate(false);
    }
  }, [id, template, loadingTemplate]);

  // Error and loading keep the page header mounted so only the card body under
  // it changes when the StackSet lands.
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title={dep?.name || 'StackSet'}
            subtitle={dep?.description || 'CloudFormation StackSet'}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={refresh} />
        </motion.div>
      </motion.div>
    );
  }
  if (!dep) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title="StackSet"
            subtitle="CloudFormation StackSet"
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSet…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  const parse = (raw, fallback) => { try { return JSON.parse(raw || ''); } catch { return fallback; } };
  // Target lists are read through parseList, never parse, because "absent" reaches
  // this page in two shapes: a missing/empty column (parse's catch fallback fires)
  // and a Go nil slice, which marshals to the literal `null` — and JSON.parse('null')
  // SUCCEEDS, so the catch never fires and `.length`/`.map` would hit null. Both
  // sides need it: a StackSet created before accounts were targetable has no
  // targetAccounts, and an accounts-only StackSet stores no OUs.
  const parseList = (raw) => { const v = parse(raw, []); return Array.isArray(v) ? v : []; };
  const ous = parseList(dep.targetOus);
  const accounts = parseList(dep.targetAccounts);
  const regions = parse(dep.regions, []);
  const parameters = parse(dep.parameters, []);
  const failedCells = instances.filter((r) => r.phase === 'failed');
  // Cancelled cells were never attempted (failure tolerance exceeded); Retry
  // covers them too, but the button names them apart from real failures.
  const cancelledCount = failedCells.filter(isCancelledCell).length;
  const realFailedCount = failedCells.length - cancelledCount;
  const plural = (n) => (n === 1 ? '' : 's');
  let retryLabel = `Retry ${realFailedCount} failed instance${plural(realFailedCount)}`;
  if (cancelledCount > 0 && realFailedCount > 0) retryLabel = `Retry ${realFailedCount} failed + ${cancelledCount} cancelled instance${plural(failedCells.length)}`;
  else if (cancelledCount > 0) retryLabel = `Retry ${cancelledCount} cancelled instance${plural(cancelledCount)}`;
  const runningOp = operations.find((o) => o.status === 'running');
  // A create that failed while resolving its targets: nothing exists in AWS,
  // so only Delete applies (the backend refuses Edit and Sync on it too).
  const neverDeployed = dep.status === 'failed' && !dep.awsStackSetId && instances.length === 0;
  // A deleted StackSet is read-only: every mutating action is off (the
  // backend refuses them too) and Delete itself is replaced by a note.
  const isDeleted = dep.status === 'deleted';
  const editDisabled = ACTIVE.includes(dep.status) || isDeleted || !!runningOp || neverDeployed;
  const syncDisabled = ACTIVE.includes(dep.status) || isDeleted || !!runningOp || neverDeployed;
  const exclusions = parseList(dep.excludedCells);
  const liveInstances = instances.filter((r) => r.phase !== 'deleted' && r.phase !== 'skipped');
  const autoDeployOn = !!dep.autoDeploy && ous.length > 0;
  const lastChecked = timeAgo(dep.lastReconciledAt);
  const accountLabel = (accountId) => {
    const name = accountNames[accountId]
      || accounts.find((a) => a.id === accountId)?.name;
    return name ? `${name} (${accountId})` : accountId;
  };

  const doDeleteInstance = async () => {
    if (!instDelete?.cell) return;
    const [account, region] = instDelete.cell.split('|');
    try {
      await deleteInstance(id, account, region, instDelete.retain === 'retain');
      setInstDelete(null);
      startPending('deleteInstance', `Deleting the stack instance in ${account} / ${region}. It is excluded from Sync and automatic deployment until re-included.`, { account, region });
      refresh();
    } catch (e) {
      setActionError(e.message);
      setInstDelete(null);
    }
  };

  const runningDrift = operations.some((o) => o.status === 'running' && o.type === 'detect_drift');

  const doDetectDrift = async () => {
    try {
      await detectStackSetDrift(id);
      startPending('drift', 'Drift detection started. Each instance\'s drift status updates when it finishes.');
      refresh();
    } catch (e) {
      setActionError(e.message);
    }
  };

  const doRefreshStatus = async () => {
    refreshTokenRef.current += 1;
    const token = refreshTokenRef.current;
    const cancelled = () => refreshTokenRef.current !== token;
    setRefreshing(true);
    try {
      const res = await refreshStackSetStatus(id);
      // The StackSet is claimed ("Refreshing status…") before the request
      // returns, so this shows the progress bar and starts the page poll.
      refresh();
      const job = await waitForJob(res?.data, () => getRefreshStatus(id), cancelled);
      if (!job) return;
      if (job.status !== 'succeeded') {
        setActionError(job.error ? `Status refresh failed: ${job.error}` : 'Status refresh failed.');
        refresh();
        return;
      }
      const r = job.refresh || {};
      const parts = [];
      if (r.missing) parts.push(`${r.missing} missing stack${r.missing === 1 ? '' : 's'}`);
      if (r.roleMissing) parts.push(`${r.roleMissing} instance${r.roleMissing === 1 ? '' : 's'} with a missing execution role`);
      if (r.inserted) parts.push(`${r.inserted} instance${r.inserted === 1 ? '' : 's'} found only in AWS`);
      if (r.removed) parts.push(`${r.removed} removed outside Prism`);
      // Instances in AWS outside this StackSet's targets: shown, never adopted
      // (so Sync / automatic deployment can never remove them).
      const external = Array.isArray(r.external) ? r.external : [];
      const externalNote = external.length
        ? ` ${external.length} instance${external.length === 1 ? '' : 's'} in AWS ${external.length === 1 ? 'is' : 'are'} outside this StackSet's targets and ${external.length === 1 ? 'was' : 'were'} left untouched: ${external.slice(0, 10).map((c) => `${c.accountId}/${c.region}`).join(', ')}${external.length > 10 ? ', …' : ''}.`
        : '';
      setNotice((parts.length
        ? `Status refreshed from AWS: ${parts.join(', ')}.`
        : `Status refreshed from AWS: ${r.checked || 0} stack${r.checked === 1 ? '' : 's'} checked, nothing changed outside Prism.`) + externalNote);
      if (Array.isArray(r.warnings) && r.warnings.length > 0) {
        setActionError(`Some checks could not run: ${r.warnings.join('; ')}`);
      }
      refresh();
    } catch (e) {
      if (!cancelled()) setActionError(e.message);
    } finally {
      if (!cancelled()) setRefreshing(false);
    }
  };

  const doRepair = async () => {
    if (!repairTarget) return;
    setRepairing(true);
    try {
      await repairInstance(id, repairTarget.accountId, repairTarget.region);
      startPending('repair', `Repairing ${repairTarget.accountId} / ${repairTarget.region}.`, { account: repairTarget.accountId, region: repairTarget.region });
      setRepairTarget(null);
      refresh();
    } catch (e) {
      setActionError(e.message);
      setRepairTarget(null);
    } finally {
      setRepairing(false);
    }
  };

  const toggleDrift = (r) => {
    const key = `${r.accountId}|${r.region}`;
    setDriftOpen((m) => ({ ...m, [key]: !m[key] }));
  };

  const doReinclude = async (x) => {
    try {
      const res = await removeExclusions(id, [{ accountId: x.accountId, region: x.region }]);
      setNotice(res?.data?.message || (autoDeployOn ? 'Will deploy automatically within 15 minutes.' : 'Run Sync to deploy.'));
      refresh();
    } catch (e) {
      setActionError(e.message);
    }
  };

  const doDelete = async () => {
    try {
      await deleteStackSet(id, retain === 'retain');
      setDeleteOpen(false);
      setConfirmText('');
      startPending('deleteStackSet', retain === 'retain'
        ? 'Deleting the StackSet. Its stacks are retained in the member accounts.'
        : 'Deleting the StackSet and its stacks in every targeted account and region.');
      refresh();
    } catch (e) {
      setActionError(e.message);
      setDeleteOpen(false);
    }
  };

  const closeSync = () => {
    syncTokenRef.current += 1;
    setSyncOpen(false);
    setSyncPlan(null);
    setSyncError(null);
    setSyncLoading(false);
  };

  // The dry run resolves the stored targets (an org walk for OU targets), so
  // the dialog opens straight away in a loading state and the plan replaces it.
  const openSync = async () => {
    syncTokenRef.current += 1;
    const token = syncTokenRef.current;
    const cancelled = () => syncTokenRef.current !== token;
    setSyncPlan(null);
    setSyncError(null);
    setSyncLoading(true);
    setSyncOpen(true);
    try {
      const res = await syncStackSet(id, { dryRun: true });
      const job = await waitForJob(res?.data, () => getSyncPreview(id), cancelled);
      if (!job) return;
      if (job.status === 'succeeded') {
        setSyncPlan(job.syncPlan || { addCells: [], removeCells: [], flaggedAccounts: [] });
      } else {
        setSyncError(job.error || 'The sync preview could not be computed.');
      }
    } catch (e) {
      if (!cancelled()) setSyncError(e.message);
    } finally {
      if (!cancelled()) setSyncLoading(false);
    }
  };

  // The real Sync re-plans server-side (it never trusts this preview) and
  // returns as soon as the StackSet is claimed; progress and any failure show
  // on the StackSet itself.
  const runSync = async () => {
    setSyncStarting(true);
    try {
      await syncStackSet(id, { dryRun: false, retainStacks: syncRetain === 'retain' });
      closeSync();
      startPending('sync', 'Sync started. Target accounts are resolved again first; progress shows above.');
      refresh();
    } catch (e) {
      setActionError(e.message);
      closeSync();
    } finally {
      setSyncStarting(false);
    }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LayersIcon sx={{ fontSize: 24 }} />}
          title={dep.name}
          subtitle={dep.description || 'CloudFormation StackSet'}
        />
      </motion.div>
      {pending && (
        <motion.div variants={sectionVariants}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setPending(null)}>{pending.text}</Alert>
        </motion.div>
      )}
      {notice && (
        <motion.div variants={sectionVariants}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setNotice(null)}>{notice}</Alert>
        </motion.div>
      )}
      {actionError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setActionError(null)}>{actionError}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Box sx={{ ...actionRowSx, mb: 2 }}>
          <Chip size="small" label={formatStatusLabel(dep.status)} sx={statusChipSx(intentFromColor(statusColor(dep.status)))} />
          <Button size="small" variant="outlined" disabled={editDisabled} onClick={() => navigate(`/stacksets/${id}/edit`)}>
            Edit
          </Button>
          {ACTIVE.includes(dep.status) && (
            <Box sx={{ flex: '1 1 260px', minWidth: 0, maxWidth: 420 }}>
              <OperationProgress percent={dep.progress} label={dep.progressLabel || 'Working…'} />
            </Box>
          )}
          {dep.errorMessage && (
            <Alert severity="warning" sx={{ flex: '1 1 260px', minWidth: 0, borderRadius: '8px' }}>
              {dep.errorMessage}
              {neverDeployed && ' Nothing was created in AWS. Delete this StackSet and create it again.'}
            </Alert>
          )}
        </Box>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); if (v === 3) loadTemplate(); }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={tabsSx}
          >
            <Tab label="Overview" sx={tabSx} />
            <Tab label={`Instances (${instances.length})`} sx={tabSx} />
            <Tab label="Operations" sx={tabSx} />
            <Tab label="Template" sx={tabSx} />
            <Tab label="Settings" sx={tabSx} />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* Automatic deployment (OU targets only). */}
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: dep.autoDeployNote ? 1 : 3 }}>
                <Box component="span" sx={{ fontWeight: 600, color: CK.navy }}>Automatic deployment:</Box>{' '}
                {ous.length === 0 ? 'Not applicable (account targets only)'
                  : autoDeployOn
                    ? `On${dep.retainOnAccountRemoval ? ' · retain stacks on removal' : ''}${lastChecked ? ` · last checked ${lastChecked}` : ' · not checked yet'}`
                    : 'Off'}
              </Typography>
              {dep.autoDeployNote && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3, borderRadius: '8px' }}
                  action={(
                    <Button color="inherit" size="small" disabled={syncDisabled} onClick={openSync}>
                      Review in Sync
                    </Button>
                  )}
                >
                  {dep.autoDeployNote}
                </Alert>
              )}
              {/* OUs and individual accounts are both targetable, so this section
                  shows whichever of the two the StackSet actually uses — a group
                  with nothing in it is omitted rather than shown as a zero, and
                  the empty state only appears when BOTH are empty. */}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Targets</Typography>
              <Box sx={{ mb: 3 }}>
                {ous.length === 0 && accounts.length === 0 && (
                  <Typography sx={emptyTextSx}>No targets.</Typography>
                )}
                {ous.length > 0 && (
                  <Box sx={{ mb: accounts.length > 0 ? 2 : 0 }}>
                    <Typography sx={targetGroupLabelSx}>Organizational units</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {ous.map((ou) => <Chip key={ou.id} size="small" label={`${ou.name} (${ou.id})`} sx={statusChipSx('info')} />)}
                    </Box>
                  </Box>
                )}
                {accounts.length > 0 && (
                  <Box>
                    <Typography sx={targetGroupLabelSx}>Accounts</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {accounts.map((a) => <Chip key={a.id} size="small" label={a.name ? `${a.name} (${a.id})` : a.id} sx={statusChipSx('success')} />)}
                    </Box>
                  </Box>
                )}
              </Box>

              {exclusions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Exclusions ({exclusions.length})</Typography>
                  <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', mb: 1 }}>
                    Excluded instances are never created by Sync or automatic deployment. Re-including deploys
                    nothing by itself — {autoDeployOn ? 'automatic deployment picks it up within 15 minutes.' : 'run Sync to deploy.'}
                  </Typography>
                  <TableContainer sx={{ ...insetPanelSx, p: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={tableHeadRowSx}>
                          <TableCell sx={headCellSx}>Account</TableCell>
                          <TableCell sx={headCellSx}>Region</TableCell>
                          <TableCell sx={headCellSx}>Reason</TableCell>
                          <TableCell sx={headCellSx}>By</TableCell>
                          <TableCell sx={headCellSx}>When</TableCell>
                          <TableCell sx={headCellSx} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exclusions.map((x, index) => (
                          <TableRow key={`${x.accountId}|${x.region}`} hover sx={rowSx(index)}>
                            <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{accountLabel(x.accountId)}</TableCell>
                            <TableCell sx={bodyCellSx}>{x.region === '*' ? 'All regions' : x.region}</TableCell>
                            <TableCell sx={bodyCellSx}>{EXCLUSION_REASON_LABELS[x.reason] || formatStatusLabel(x.reason)}</TableCell>
                            <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{x.byEmail || '—'}</TableCell>
                            <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{x.at ? new Date(x.at).toLocaleString() : '—'}</TableCell>
                            <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }} align="right">
                              <Button size="small" disabled={dep.status === 'deleted'} onClick={() => doReinclude(x)}>Re-include</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Regions</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {regions.length === 0 && <Typography sx={emptyTextSx}>No regions selected.</Typography>}
                {regions.map((r) => <Chip key={r} size="small" label={r} sx={statusChipSx('neutral')} />)}
              </Box>

              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Parameters</Typography>
              <TableContainer sx={{ maxWidth: 560 }}>
                <Table size="small">
                  <TableBody>
                    {parameters.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={emptyTextSx}>This template takes no parameters.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {parameters.map((p, index) => (
                      <TableRow key={p.key} hover sx={rowSx(index)}>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.key}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{p.noEcho ? '••••••••' : p.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', mt: 3 }}>
                Template v{dep.templateVersion} · Created by {dep.createdByEmail} · {new Date(dep.createdAt).toLocaleString()}
              </Typography>
            </Box>
          )}

          {tab === 1 && (
            <>
              {!isDeleted && (failedCells.length > 0 || liveInstances.length > 0) && (
                <Box sx={{ ...actionRowSx, p: 2, borderBottom: `1px solid ${CK.border}` }}>
                  {liveInstances.length > 0 && (
                    <Button variant="outlined" disabled={editDisabled} onClick={doDetectDrift}>
                      {runningDrift ? 'Detecting drift…' : 'Detect drift'}
                    </Button>
                  )}
                  {liveInstances.length > 0 && (
                    <Button variant="outlined" disabled={editDisabled || refreshing} onClick={doRefreshStatus}>
                      {refreshing ? 'Refreshing…' : 'Refresh status from AWS'}
                    </Button>
                  )}
                  {failedCells.length > 0 && (
                    <Button variant="outlined" color="warning" disabled={editDisabled} onClick={async () => {
                      try { await retryInstances(id); startPending('retry', 'Retry started.'); refresh(); } catch (e) { setActionError(e.message); }
                    }}>
                      {retryLabel}
                    </Button>
                  )}
                  {liveInstances.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={editDisabled}
                      onClick={() => setInstDelete({ cell: `${liveInstances[0].accountId}|${liveInstances[0].region}`, retain: 'delete' })}
                    >
                      Delete instance…
                    </Button>
                  )}
                </Box>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      <TableCell sx={{ ...headCellSx, width: 40 }} />
                      <TableCell sx={headCellSx}>Account</TableCell><TableCell sx={headCellSx}>Region</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell><TableCell sx={headCellSx}>Reason</TableCell>
                      <TableCell sx={headCellSx}>Drift</TableCell><TableCell sx={headCellSx}>Detailed status</TableCell>
                      <TableCell sx={headCellSx} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instances.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={INSTANCE_COLS} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={emptyTextSx}>
                            {isDeleted
                              ? 'This StackSet has been deleted, so it has no stack instances.'
                              : 'No stack instances yet — they appear once the StackSet deploys to your target accounts.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {instances.map((r, index) => {
                      const cellKey = `${r.accountId}|${r.region}`;
                      const drifted = r.driftStatus === 'DRIFTED';
                      return (
                        <Fragment key={r.id}>
                          <InstanceEventsRow
                            instance={r}
                            stackSetId={id}
                            index={index}
                            colSpan={INSTANCE_COLS}
                            extraCells={(
                              <>
                                <TableCell sx={bodyCellSx}>
                                  <Chip
                                    size="small"
                                    label={driftLabel(r.driftStatus)}
                                    sx={{ ...statusChipSx(driftIntent(r.driftStatus)), ...(drifted ? { cursor: 'pointer' } : {}) }}
                                    onClick={drifted ? () => toggleDrift(r) : undefined}
                                    title={r.lastDriftCheckAt ? `Checked ${new Date(r.lastDriftCheckAt).toLocaleString()}` : undefined}
                                  />
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                  {r.detailedStatus ? formatStatusLabel(r.detailedStatus) : '—'}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: 'right', whiteSpace: 'nowrap' }} align="right">
                                  {drifted && (
                                    <Button size="small" onClick={() => toggleDrift(r)}>
                                      {driftOpen[cellKey] ? 'Hide drift' : 'Resource drift'}
                                    </Button>
                                  )}
                                  {REPAIRABLE.includes(r.phase) && (
                                    <Button size="small" color="warning" disabled={editDisabled} onClick={() => setRepairTarget(r)}>
                                      Repair
                                    </Button>
                                  )}
                                </TableCell>
                              </>
                            )}
                          />
                          {drifted && driftOpen[cellKey] && (
                            <TableRow>
                              <TableCell colSpan={INSTANCE_COLS} sx={{ py: 0, borderBottom: `1px solid ${CK.border}` }}>
                                <ResourceDriftPanel stackSetId={id} account={r.accountId} region={r.region} />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === 2 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={{ ...headCellSx, width: 40 }} />
                    <TableCell sx={headCellSx}>Type</TableCell><TableCell sx={headCellSx}>Status</TableCell><TableCell sx={headCellSx}>Requested by</TableCell>
                    <TableCell sx={headCellSx}>Started</TableCell><TableCell sx={headCellSx}>Completed</TableCell><TableCell sx={headCellSx} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No operations yet — deployments, updates and syncs are recorded here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {operations.map((o, index) => (
                    <OperationResultsRow
                      key={o.id}
                      operation={o}
                      stackSetId={id}
                      index={index}
                      onStop={async (op) => {
                        try { await stopOperation(id, op.id); setNotice('Stop requested'); refresh(); } catch (e) { setActionError(e.message); }
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <Box sx={{ p: 3 }}>
              {templateError ? (
                <Alert
                  severity="error"
                  sx={{ borderRadius: '8px' }}
                  action={<Button color="inherit" size="small" onClick={loadTemplate}>Retry</Button>}
                >
                  Failed to load template: {templateError}
                </Alert>
              ) : template === null ? <PortalLoading label="Loading template…" /> : (
                <Box
                  component="pre"
                  sx={{
                    ...insetPanelSx,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    overflow: 'auto',
                    maxHeight: 480,
                    m: 0,
                  }}
                >
                  {template}
                </Box>
              )}
              <Button variant="outlined" sx={{ mt: 2 }} disabled={editDisabled} onClick={() => navigate(`/stacksets/${id}/edit`)}>
                Update template
              </Button>
            </Box>
          )}

          {tab === 4 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Target sync</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
                {autoDeployOn
                  ? 'Automatic deployment is on: accounts added to (or removed from) the target OUs are reconciled within 15 minutes, and large removals are held for review here. Run Sync to reconcile now.'
                  : 'Automatic deployment is off: accounts added to (or removed from) the target OUs after deployment are not handled automatically. Run Sync to reconcile, or turn on automatic deployment from Edit.'}
                {' '}An account you targeted explicitly is never removed by Sync: if it stops
                qualifying, its stacks are kept and the account is reported so you can decide.
              </Typography>
              <Button variant="outlined" disabled={syncDisabled} onClick={openSync}>Preview sync</Button>

              <Typography sx={{ ...sectionHeadingSx, mt: 4, mb: 1 }}>Danger zone</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
                Deleting a StackSet removes it from Prism and, unless you retain them, destroys
                its stacks in every targeted account and region.
              </Typography>
              {isDeleted ? (
                <Typography sx={emptyTextSx}>This StackSet has been deleted.</Typography>
              ) : (
                <Button variant="outlined" color="error" disabled={ACTIVE.includes(dep.status) || !!runningOp} onClick={() => setDeleteOpen(true)}>
                  Delete StackSet
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* Repair one instance found missing by Refresh status. */}
      <Dialog open={!!repairTarget} onClose={() => { if (!repairing) setRepairTarget(null); }} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Repair stack instance</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...dialogBodyTextSx, mt: 1, mb: 1 }}>
            {repairTarget?.accountId} / {repairTarget?.region}
          </Typography>
          {repairTarget?.statusReason && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>{repairTarget.statusReason}</Alert>
          )}
          <Typography sx={dialogBodyTextSx}>
            {repairTarget?.phase === 'role_missing'
              ? 'Prism will re-create CKPrism-StackSetExecutionRole in this account, wait until the administration role can assume it, and then redeploy this stack instance.'
              : 'The member stack was deleted outside Prism. Prism will clear the StackSet\'s record of this instance (keeping nothing, since the stack is already gone) and deploy it again from the current template and parameters.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRepairTarget(null)} disabled={repairing}>Cancel</Button>
          <Button color="warning" variant="contained" disabled={repairing} onClick={doRepair}>Repair</Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog: AWS parity — Delete vs Retain stacks + type-to-confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Delete StackSet “{dep.name}”</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={retain} onChange={(e) => setRetain(e.target.value)} sx={{ mt: 1 }}>
            <FormControlLabel value="delete" control={<Radio />} label="Delete stacks — destroys the template's resources in every account/region" />
            <FormControlLabel value="retain" control={<Radio />} label="Retain stacks — deployed resources stay (and keep billing) in member accounts" />
          </RadioGroup>
          {retain === 'delete' && (
            <Alert severity="error" sx={{ my: 1, borderRadius: '8px' }}>
              All resources created by this template will be permanently destroyed.
            </Alert>
          )}
          <TextField
            fullWidth size="small" sx={{ mt: 1 }}
            label={`Type ${dep.name} to confirm`}
            value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={confirmText !== dep.name} onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete ONE instance: Retain vs Delete (the delete-StackSet copy). The
          cell is excluded first, so Sync / automatic deployment never re-add it. */}
      <Dialog open={!!instDelete} onClose={() => setInstDelete(null)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Delete stack instance</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            select fullWidth size="small" sx={{ mt: 1 }}
            label="Stack instance"
            value={instDelete?.cell || ''}
            onChange={(e) => setInstDelete((cur) => ({ ...cur, cell: e.target.value }))}
          >
            {liveInstances.map((r) => (
              <MenuItem key={r.id} value={`${r.accountId}|${r.region}`}>
                {accountLabel(r.accountId)} · {r.region} · {formatStatusLabel(r.phase)}
              </MenuItem>
            ))}
          </TextField>
          <RadioGroup
            value={instDelete?.retain || 'delete'}
            onChange={(e) => setInstDelete((cur) => ({ ...cur, retain: e.target.value }))}
            sx={{ mt: 1 }}
          >
            <FormControlLabel value="delete" control={<Radio />} label="Delete stack — destroys the template's resources in this account/region" />
            <FormControlLabel value="retain" control={<Radio />} label="Retain stack — deployed resources stay (and keep billing) in the member account" />
          </RadioGroup>
          {instDelete?.retain !== 'retain' && (
            <Alert severity="error" sx={{ my: 1, borderRadius: '8px' }}>
              All resources this template created in that account and region will be permanently destroyed.
            </Alert>
          )}
          <Typography sx={{ ...dialogBodyTextSx, mt: 1 }}>
            The instance is added to this StackSet&apos;s exclusions, so Sync and automatic deployment will not
            re-create it. Re-include it from the Overview tab to deploy it again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setInstDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!instDelete?.cell} onClick={doDeleteInstance}>Delete instance</Button>
        </DialogActions>
      </Dialog>

      {/* Sync dialog: shows the add/remove diff before executing */}
      <Dialog open={syncOpen} onClose={closeSync} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Sync targets</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {syncLoading && (
            <Box sx={{ mt: 1 }}>
              <OperationProgress label="Resolving target accounts…" />
              <Typography sx={dialogBodyTextSx}>
                Prism is reading the target OUs from your organization to work out what Sync would change.
                On a large organization this can take a minute.
              </Typography>
            </Box>
          )}
          {syncError && (
            <Alert severity="error" sx={{ mt: 1, borderRadius: '8px' }}>
              The sync preview could not be computed: {syncError}
            </Alert>
          )}
          {/* Optional chaining on every list: the backend marshals a nil slice to
              JSON `null`, so a present-but-partial plan would otherwise throw here
              and take the whole Detail page down, not just this dialog. */}
          {syncPlan && !syncPlan.addCells?.length && !syncPlan.removeCells?.length
            && !syncPlan.flaggedAccounts?.length && (
            <Alert severity="success" sx={{ mt: 1, borderRadius: '8px' }}>Everything is in sync — no changes needed.</Alert>
          )}
          {syncPlan?.note && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1, borderRadius: '8px' }}>{syncPlan.note}</Alert>
          )}
          {syncPlan?.flaggedAccounts?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1, borderRadius: '8px' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                Kept, not removed ({syncPlan.flaggedAccounts.length})
              </Typography>
              {syncPlan.flaggedAccounts.map((f) => (
                <Typography key={f.accountId} sx={dialogBodyTextSx}>
                  {f.name ? `${f.name} (${f.accountId})` : f.accountId} — {f.reason}
                </Typography>
              ))}
              <Typography sx={{ ...dialogBodyTextSx, mt: 0.5 }}>
                These accounts are targeted explicitly, so their stacks are left in place. Remove them from
                the StackSet&apos;s targets if you want their stacks deleted.
              </Typography>
            </Alert>
          )}
          {syncPlan?.addCells?.length > 0 && (
            <>
              <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mt: 2, mb: 1 }}>
                Will deploy ({syncPlan.addCells.length})
              </Typography>
              {syncPlan.addCells.map((c) => (
                <Typography key={`${c.accountId}-${c.region}`} sx={dialogBodyTextSx}>{c.accountId} · {c.region}</Typography>
              ))}
            </>
          )}
          {syncPlan?.removeCells?.length > 0 && (
            <>
              <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mt: 2, mb: 1 }}>
                Will remove ({syncPlan.removeCells.length})
              </Typography>
              {syncPlan.removeCells.map((c) => (
                <Typography key={`${c.accountId}-${c.region}`} sx={dialogBodyTextSx}>{c.accountId} · {c.region}</Typography>
              ))}
              <RadioGroup value={syncRetain} onChange={(e) => setSyncRetain(e.target.value)} sx={{ mt: 1 }}>
                <FormControlLabel value="delete" control={<Radio />} label="Delete the removed accounts' stacks" />
                <FormControlLabel value="retain" control={<Radio />} label="Retain the removed accounts' stacks" />
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeSync}>Cancel</Button>
          {syncError && <Button onClick={openSync}>Try again</Button>}
          <Button
            variant="contained"
            disabled={syncStarting || !syncPlan || (!syncPlan.addCells?.length && !syncPlan.removeCells?.length)}
            onClick={runSync}
          >
            Sync now
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
